import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useNotes, useCreateNote, useDeleteNote } from '../api/hooks'
import { useUIStore } from '../store/ui'
import { NoteEditor } from '../components/notes/NoteEditor'
import { Button } from '../components/primitives/Button'
import { useToast } from '../components/primitives/Toast'

/**
 * Desktop notes page — the editor half. The list lives in NotesListPanel,
 * beside the main sidebar, which this page keeps open.
 */
export function NotesPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const openNotesPanel = useUIStore((s) => s.openNotesPanel)
  const { data: notes = [], isLoading } = useNotes()
  const createNote = useCreateNote()
  const deleteNote = useDeleteNote()
  const { showToast } = useToast()

  useEffect(() => {
    openNotesPanel()
  }, [openNotesPanel])

  if (isLoading) {
    return <PageMessage>Loading…</PageMessage>
  }

  // Landing on /notes picks up where the user left off: the newest note.
  if (!id && notes.length > 0) {
    return <Navigate to={`/notes/${notes[0].id}`} replace />
  }

  if (!id) {
    return (
      <PageMessage>
        <p style={{ margin: '0 0 16px' }}>No notes yet.</p>
        <Button
          variant="bracketed"
          loading={createNote.isPending}
          onClick={() =>
            createNote.mutate(
              {},
              {
                onSuccess: (note) => navigate(`/notes/${note.id}`),
                onError: () => showToast('Failed to create note', 'error'),
              }
            )
          }
        >
          new note
        </Button>
      </PageMessage>
    )
  }

  const note = notes.find((n) => n.id === id)
  if (!note) {
    return <PageMessage>That note no longer exists.</PageMessage>
  }

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <NoteEditor
        note={note}
        onDeleted={() =>
          deleteNote.mutate(note.id, {
            onSuccess: () => navigate('/notes'),
            onError: () => showToast('Failed to delete note', 'error'),
          })
        }
      />
    </div>
  )
}

function PageMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        fontFamily: 'var(--font-serif)',
        fontStyle: 'italic',
        color: 'var(--color-ink-muted)',
      }}
    >
      {children}
    </div>
  )
}
